import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';
import { useAuth } from '@/hooks/useAuth';
import { filaQualidadeOptions } from '@/hooks/useQualidadeInbox';

interface CicloDaConversa {
  etapa: string | null;
  nome: string | null;
  resumo: string | null;
  pedidos?: string[];
  outras?: Array<{ etapa: string; nome: string; pedidos: string[] }>;
}

/* A ETAPA DO CICLO NO TOPO DA CONVERSA (Douglas 23/09/2026).
 *
 * "Eu não sei qual é a etapa dela do ciclo... não está aparecendo na lista e
 *  no topo não tem." A faixa de cima diz a fila do PEDIDO no Kanban; esta diz
 * em que etapa do CICLO DA QUALIDADE o cliente está — e são coisas diferentes:
 * o pedido pode estar FINALIZADO 100% e o cliente ainda na Verificação do eSIM.
 *
 * Mesma chave de cache do botão "Transferir para a IsabellA" e mesma régua do
 * Painel que ela lê a cada turno: tela, botão e IA dizem a mesma etapa.
 * Sem etapa aberta a faixa não existe, em vez de ficar vazia.
 */
export function CicloQualidadeFaixa({ conversaId }: { conversaId: string | null | undefined }) {
  const { data: ciclo } = useQuery({
    queryKey: ['ciclo-ia-conversa', conversaId], enabled: !!conversaId, staleTime: 30_000,
    queryFn: () => requisitarAtivacao<CicloDaConversa>(`conversas/${conversaId}/ciclo-ia`),
  });
  // ⛔ O SELO ANDA JUNTO COM A LISTA (Douglas 24/09/2026): o cliente validou o
  // eSIM, a lista virou BV e o topo seguiu em "Verificação do eSIM" — o selo
  // buscava a etapa uma vez só. Toda vez que a etiqueta da lista se renova
  // (mesma consulta, mesmo cache), o selo busca de novo.
  const { profile } = useAuth();
  const client = useQueryClient();
  const { dataUpdatedAt } = useQuery(filaQualidadeOptions(profile?.id));
  useEffect(() => {
    if (conversaId && dataUpdatedAt) void client.invalidateQueries({ queryKey: ['ciclo-ia-conversa', conversaId] });
  }, [client, conversaId, dataUpdatedAt]);
  if (!ciclo?.etapa) return null;
  const ped = (p?: string[]) => (p?.length ? ` · pedido ${p.join(', ')}` : '');
  const outras = ciclo.outras || [];
  /* ⛔ SELO NO CABEÇALHO, NÃO FAIXA (Douglas 23/09/2026): "não coloque ela
     assim, coloque dentro do painel, tem espaço... mais direto". A etapa fica
     ao lado do Finalizar; o resumo e os pedidos, no passar do mouse. */
  const titulo = [`${ciclo.nome}: ${ciclo.resumo || ''}${ped(ciclo.pedidos)}`, ...outras.map(o => `Também ${o.nome}${ped(o.pedidos)}`)].join('\n');
  return (
    <span title={titulo}
      className="inline-flex h-7 max-w-[220px] flex-shrink-0 items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 text-[11px] font-semibold text-primary">
      <span className="truncate">Ciclo · {ciclo.nome}</span>
      {outras.length > 0 && <span className="flex-shrink-0 text-[10px] font-medium text-primary/70">+{outras.length}</span>}
    </span>
  );
}
